/**
 * Confetti celebration utility
 * Uses canvas-confetti library loaded via CDN
 * Gold (#a69373) and Teal (#5a8a8a) colors to match app aesthetic
 */

// App brand colors for confetti
const CONFETTI_COLORS = ['#a69373', '#d4b87a', '#5a8a8a', '#4a7a7a', '#e8dcc8'];

/**
 * Fire a celebration confetti burst
 * @param {string} type - Type of celebration: 'checkin', 'badge', 'quest', 'meditation', 'streak'
 */
export function fireConfetti(type = 'default') {
  // Check if confetti is available (loaded via CDN)
  if (typeof window.confetti !== 'function') {
    console.warn('Confetti library not loaded');
    return;
  }

  const confetti = window.confetti;

  switch (type) {
    case 'badge':
    case 'streak':
      // Special burst for badges and streaks - more dramatic
      const duration = 3000;
      const end = Date.now() + duration;

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.7 },
          colors: CONFETTI_COLORS
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.7 },
          colors: CONFETTI_COLORS
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      }());
      break;

    case 'quest':
      // Fireworks style for quest completion
      confetti({
        particleCount: 100,
        spread: 100,
        origin: { y: 0.6 },
        colors: CONFETTI_COLORS,
        startVelocity: 45,
        gravity: 1.2,
        ticks: 200
      });
      
      // Delayed second burst
      setTimeout(() => {
        confetti({
          particleCount: 50,
          spread: 80,
          origin: { y: 0.5, x: 0.3 },
          colors: CONFETTI_COLORS
        });
        confetti({
          particleCount: 50,
          spread: 80,
          origin: { y: 0.5, x: 0.7 },
          colors: CONFETTI_COLORS
        });
      }, 200);
      break;

    case 'meditation':
      // Gentle, calming burst for meditation
      confetti({
        particleCount: 40,
        spread: 70,
        origin: { y: 0.7 },
        colors: CONFETTI_COLORS,
        startVelocity: 25,
        gravity: 0.8,
        ticks: 150,
        shapes: ['circle'],
        scalar: 0.8
      });
      break;

    case 'checkin':
    default:
      // Standard celebration burst
      confetti({
        particleCount: 60,
        spread: 70,
        origin: { y: 0.7 },
        colors: CONFETTI_COLORS,
        startVelocity: 30,
        gravity: 1,
        ticks: 100
      });
      break;
  }
}

/**
 * Fire confetti from a specific element's position
 * @param {HTMLElement} element - Element to fire confetti from
 * @param {string} type - Type of celebration
 */
export function fireConfettiFromElement(element, type = 'default') {
  if (typeof window.confetti !== 'function' || !element) {
    return;
  }

  const rect = element.getBoundingClientRect();
  const x = (rect.left + rect.width / 2) / window.innerWidth;
  const y = (rect.top + rect.height / 2) / window.innerHeight;

  window.confetti({
    particleCount: 50,
    spread: 60,
    origin: { x, y },
    colors: CONFETTI_COLORS,
    startVelocity: 25,
    gravity: 1,
    ticks: 100
  });
}

export default fireConfetti;
